import {createRule, type Maybe} from '@regle/core';
import {useFormat, useTranslation} from '#imports';
import {useImageDimensionsValidator, type ImageDimensionsMetadata} from './common/dimensions.validator';

/**
 * Validates if an image is within the minimum dimensions.
 * @param value - The image to validate.
 * @param minWidth - The minimum width of the image.
 * @param minHeight - The minimum height of the image.
 * @returns True if the image is within the minimum dimensions, false otherwise.
 *
 * @example
 * ```ts
 * const {r$} = useRegle(... , {
 *    image: {minImageDimensions: minImageDimensions(400, 200)}
 * })
 * ```
 */
export const minImageDimensions = createRule({
    async validator(value: Maybe<File>, minWidth: number, minHeight: number): Promise<ImageDimensionsMetadata> {
        return await useImageDimensionsValidator({value, widthLimit: minWidth, heightLimit: minHeight, type: 'min'});
    },
    message(metadata) {
        const {t} = useTranslation();
        const {formatNumber} = useFormat();

        if (metadata.errorType === 'invalid-image-dimensions') {
            const [widthLimit, heightLimit] = metadata.$params;
            return t('generic.forms.validation.errors.minImageDimensions', {
                width: formatNumber(metadata.width),
                height: formatNumber(metadata.height),
                minWidth: formatNumber(widthLimit),
                minHeight: formatNumber(heightLimit),
            });
        } else if (metadata.errorType === 'image-dimensions-unreadable') {
            return t('generic.forms.validation.errors.image.dimensions.unreadable');
        }
        return t('generic.forms.validation.errors.common');
    },
});
